import { useState, useEffect } from 'preact/hooks';

import { InternalClientLogin } from '@phonepe/internal-login-sdk';

import Api from '@manage-cards/services/Api';
import { getUrlParams } from '@manage-cards/utils/helpers';
import { Global, LOGIN_SDK_CONFIG } from '@manage-cards/constants';
import { IUserSummaryDetails, IUserSummaryPayload } from '@manage-cards/types';

export const usePhonePeLogin = () => {
	const [isLoggedIn, setIsLoggedIn] = useState(
		() => !!localStorage.getItem(Global.AUTH_TOKEN),
	);
	const [isInitializing, setIsInitializing] = useState(true);
	const [userDetails, setUserDetails] = useState<IUserSummaryDetails>();

	const getLoginClientInstance = () =>
		InternalClientLogin.getInstance(LOGIN_SDK_CONFIG);

	const loginViaPhonePe = () => {
		const instance = getLoginClientInstance();
		instance.redirectToLogin();
	};

	const clearLoginData = () => {
		localStorage.removeItem(Global.AUTH_TOKEN);
		localStorage.removeItem(Global.USER_ID);
	};

	const getUserDetails = async (userId: string) => {
		try {
			const payload: IUserSummaryPayload = {
				userId,
				validateState: true,
				fullVpa: true,
				org: Global.USER_ORG,
				userStateRequired: true,
				excludedBlacklisted: true,
			};
			const response = await Api.getUserSummary(payload);
			setUserDetails(response);
			setIsLoggedIn(true);
			setIsInitializing(false);
		} catch (error) {
			console.error('Error fetching user details:', error);
			setIsLoggedIn(false);
			setIsInitializing(false);
			clearLoginData();
			loginViaPhonePe();
		}
	};

	const getLoginToken = async () => {
		try {
			const instance = getLoginClientInstance();
			const tokenRes = await instance.getToken({
				type: '1FA',
			});
			const token = tokenRes.tokenResponses?.[0]?.token;
			localStorage.setItem(Global.AUTH_TOKEN, token ?? '');
			setIsLoggedIn(!!token);
			getUserDetails(tokenRes.profileSummary.userId);
			localStorage.setItem(Global.USER_ID, tokenRes.profileSummary.userId);

			window.history.replaceState(
				{},
				'',
				window.location.origin + window.location.pathname,
			);
		} catch (error) {
			console.error('Error fetching login token:', error);
			setIsLoggedIn(false);
			clearLoginData();
		}
	};

	const logout = () => {
		const instance = getLoginClientInstance();
		instance.logout(localStorage.getItem(Global.AUTH_TOKEN) ?? '');
		localStorage.clear();
		setIsLoggedIn(false);
	};

	useEffect(() => {
		if (getUrlParams('code')) {
			getLoginToken();
		} else if (localStorage.getItem(Global.AUTH_TOKEN)) {
			getUserDetails(localStorage.getItem(Global.USER_ID) ?? '');
		} else {
			setIsLoggedIn(false);
			loginViaPhonePe();
		}
	}, []);

	return { isInitializing, isLoggedIn, userDetails, loginViaPhonePe, logout };
};
